import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { Sync as SyncIcon } from '@mui/icons-material';
import { apiClient, endpoints, isGodUser } from '../api/client';

type SyncDirection = 'bidirectional' | 'local-to-remote' | 'remote-to-local';

interface SyncSummary {
  synced_to_local: number;
  synced_to_remote: number;
  settings_synced_to_local: number;
  settings_synced_to_remote: number;
  failed: number;
  dry_run: boolean;
}

interface SyncResponse {
  success: boolean;
  logs: string[];
  summary: SyncSummary;
  local_database: string;
  remote_database: string;
  sync_mode?: string;
}

export const DefinitionSync: React.FC = () => {
  const [targetPeerApiToken, setTargetPeerApiToken] = useState('');
  const [targetPeerBaseUrl, setTargetPeerBaseUrl] = useState('');
  const [targetDatabaseUrl, setTargetDatabaseUrl] = useState('');
  const [useDirectDatabase, setUseDirectDatabase] = useState(false);
  const [direction, setDirection] = useState<SyncDirection>('local-to-remote');
  const [dryRun, setDryRun] = useState(true);
  const [force, setForce] = useState(false);
  const [wipeRemote, setWipeRemote] = useState(false);
  const [syncSettings, setSyncSettings] = useState(true);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SyncResponse | null>(null);

  const canRun = isGodUser();
  const thisCollectorHost = window.location.hostname;
  const thisCollectorEnv = thisCollectorHost.includes('.authentix.dev')
    ? 'dev'
    : thisCollectorHost.includes('.authentix.com')
      ? 'prod'
      : thisCollectorHost;

  const handleRun = async () => {
    if (!useDirectDatabase && !targetPeerApiToken.trim()) {
      setError('Target environment API token is required');
      return;
    }
    if (useDirectDatabase && !targetDatabaseUrl.trim()) {
      setError('Target database URL is required for direct database sync');
      return;
    }

    if (
      wipeRemote &&
      !window.confirm(
        'This will delete ALL marketplace definitions in the target environment, then copy from this one. Continue?'
      )
    ) {
      return;
    }

    if (
      !dryRun &&
      !window.confirm(
        'This will write to the target environment. Run a dry run first if you have not already. Continue?'
      )
    ) {
      return;
    }

    setRunning(true);
    setError(null);
    setResult(null);

    try {
      const payload: Record<string, unknown> = {
        direction,
        dry_run: dryRun,
        force,
        wipe_remote: wipeRemote,
        sync_settings: syncSettings,
      };
      if (!useDirectDatabase) {
        payload.target_peer_api_token = targetPeerApiToken.trim();
      }
      if (targetPeerBaseUrl.trim()) {
        payload.target_peer_base_url = targetPeerBaseUrl.trim();
      }
      if (useDirectDatabase && targetDatabaseUrl.trim()) {
        payload.target_database_url = targetDatabaseUrl.trim();
      }

      const response = await apiClient.post<SyncResponse>(endpoints.adminSyncDefinitions, payload, {
        timeout: 300000,
      });
      setResult(response.data);
    } catch (err: unknown) {
      const detail =
        err &&
        typeof err === 'object' &&
        'response' in err &&
        err.response &&
        typeof err.response === 'object' &&
        'data' in err.response &&
        err.response.data &&
        typeof err.response.data === 'object' &&
        'detail' in err.response.data
          ? String((err.response.data as { detail: unknown }).detail)
          : 'Sync request failed';
      setError(detail);
    } finally {
      setRunning(false);
    }
  };

  if (!canRun) {
    return (
      <Alert severity="info">
        Definition sync is restricted to GOD users. Contact an administrator if you need access.
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Environment Sync
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Sync marketplace definitions and common headers/user agents to the other environment via
        scraper-to-scraper HTTPS. This deployment&apos;s database is always the local side; provide
        the target environment&apos;s service <strong>API_TOKEN</strong> (Kubernetes secret{' '}
        <code>auth</code> / <code>int_api_key</code>), not your dashboard login JWT. Peer calls go
        to <code>api.brandtrax.authentix.*</code>. Nothing is stored after the request.
      </Typography>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <TextField
            label="Target environment API token (int_api_key)"
            placeholder="From prod: kubectl get secret auth -n sipi …"
            value={targetPeerApiToken}
            onChange={e => setTargetPeerApiToken(e.target.value)}
            fullWidth
            margin="normal"
            type="password"
            autoComplete="off"
            required={!useDirectDatabase}
            helperText={
              'Service token for the other environment (secret auth → int_api_key). ' +
              'Not the storeBearer JWT from the browser. Peer URL defaults to api.* (dev → prod .com).'
            }
          />

          <TextField
            label="Target peer URL (optional override)"
            placeholder="https://api.brandtrax.authentix.com"
            value={targetPeerBaseUrl}
            onChange={e => setTargetPeerBaseUrl(e.target.value)}
            fullWidth
            margin="normal"
            helperText="Leave blank to use the default URL for the other environment."
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={useDirectDatabase}
                onChange={e => setUseDirectDatabase(e.target.checked)}
              />
            }
            label="Use direct database URL instead (local machine / sync.sh only)"
          />

          {useDirectDatabase && (
            <TextField
              label="Target database URL"
              placeholder="postgresql://user:password@host:port/scraper?sslmode=require"
              value={targetDatabaseUrl}
              onChange={e => setTargetDatabaseUrl(e.target.value)}
              fullWidth
              margin="normal"
              type="password"
              autoComplete="off"
            />
          )}

          <FormControl fullWidth margin="normal" size="small">
            <InputLabel id="sync-direction-label">Direction</InputLabel>
            <Select
              labelId="sync-direction-label"
              label="Direction"
              value={direction}
              onChange={e => setDirection(e.target.value as SyncDirection)}
            >
              <MenuItem value="bidirectional">Bidirectional — newer version wins each way</MenuItem>
              <MenuItem value="local-to-remote">
                Push: this Collector ({thisCollectorEnv}) → peer
              </MenuItem>
              <MenuItem value="remote-to-local">
                Pull: peer → this Collector ({thisCollectorEnv})
              </MenuItem>
            </Select>
            <FormHelperText>
              This page is {thisCollectorHost}. Local is always this deployment; peer is the API
              token/URL above. The old “prod to dev” examples assumed you were on dev — they are
              reversed if you run this from prod.
            </FormHelperText>
          </FormControl>

          <Box sx={{ mt: 1 }}>
            <FormControlLabel
              control={<Checkbox checked={dryRun} onChange={e => setDryRun(e.target.checked)} />}
              label="Dry run (preview only, no writes)"
            />
            <FormControlLabel
              control={<Checkbox checked={force} onChange={e => setForce(e.target.checked)} />}
              label="Force (ignore timestamps; sync all rows per direction)"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={syncSettings}
                  onChange={e => setSyncSettings(e.target.checked)}
                />
              }
              label="Include common headers & user agents"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={wipeRemote}
                  disabled={direction !== 'local-to-remote'}
                  onChange={e => setWipeRemote(e.target.checked)}
                />
              }
              label="Wipe remote definitions first (destructive; requires local → target)"
            />
          </Box>

          <Button
            variant="contained"
            startIcon={running ? <CircularProgress size={18} color="inherit" /> : <SyncIcon />}
            onClick={handleRun}
            disabled={
              running ||
              (!useDirectDatabase && !targetPeerApiToken.trim()) ||
              (useDirectDatabase && !targetDatabaseUrl.trim())
            }
            sx={{ mt: 2 }}
          >
            {running ? 'Running sync…' : dryRun ? 'Run dry run' : 'Run sync'}
          </Button>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {result && (
        <Card>
          <CardContent>
            <Alert severity={result.success ? 'success' : 'warning'} sx={{ mb: 2 }}>
              {result.summary.dry_run ? 'Dry run complete' : 'Sync complete'}
              {result.success ? '' : ' — some items failed'}
              {result.sync_mode ? ` (${result.sync_mode})` : ''}
              {' — '}
              {result.local_database} ↔ {result.remote_database}
            </Alert>
            <Typography variant="body2" gutterBottom>
              Definitions → local: {result.summary.synced_to_local}, → remote:{' '}
              {result.summary.synced_to_remote}
              {result.summary.settings_synced_to_local > 0 ||
              result.summary.settings_synced_to_remote > 0 ? (
                <>
                  {' '}
                  | Settings → local: {result.summary.settings_synced_to_local}, → remote:{' '}
                  {result.summary.settings_synced_to_remote}
                </>
              ) : null}
              {result.summary.failed > 0 ? ` | Failed: ${result.summary.failed}` : null}
            </Typography>
            <TextField
              label="Log output"
              value={result.logs.join('\n')}
              multiline
              minRows={12}
              maxRows={24}
              fullWidth
              InputProps={{ readOnly: true }}
              sx={{
                mt: 1,
                '& textarea': { fontFamily: 'monospace', fontSize: '0.8rem' },
              }}
            />
          </CardContent>
        </Card>
      )}
    </Box>
  );
};
